import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import API from '../utils/api';

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return ''; }
};

const GiftBanner = ({ childName }) => (
  <div style={{
    background: 'linear-gradient(135deg,#2d1b69 0%,#6f42c1 100%)',
    borderRadius: 20,
    padding: '28px 32px',
    textAlign: 'center',
    boxShadow: '0 8px 32px rgba(111,66,193,0.3)',
  }}>
    <div style={{ fontSize: 48, marginBottom: 12 }}>🎁</div>
    <h3 style={{ color: '#fff', fontFamily: "'Fredoka One',cursive", fontSize: 24, marginBottom: 8 }}>
      Love these stories?
    </h3>
    <p style={{ color: '#ddd6fe', fontSize: 15, marginBottom: 20, maxWidth: 420, margin: '0 auto 20px' }}>
      Give {childName} the gift of unlimited personalised bedtime stories — created just for them, every night.
    </p>
    <Link to="/gift"
      style={{
        display: 'inline-block',
        background: '#fff',
        color: '#6f42c1',
        fontWeight: 700,
        fontSize: 15,
        padding: '12px 32px',
        borderRadius: 50,
        textDecoration: 'none',
      }}>
      🌙 Gift a Subscription
    </Link>
  </div>
);

const StoryCard = ({ story }) => {
  const [expanded, setExpanded] = useState(false);
  const paragraphs = (story.content || '').split(/\n\s*\n/).filter(Boolean);

  return (
    <div style={{
      background: '#fff',
      borderRadius: 20,
      overflow: 'hidden',
      boxShadow: '0 4px 24px rgba(45,27,105,0.08)',
      border: '1px solid #ede9fe',
      transition: 'transform 0.15s, box-shadow 0.15s',
    }}>
      {/* Illustration */}
      {story.illustrationUrl && (
        <div style={{ position: 'relative', paddingBottom: '52%', overflow: 'hidden' }}>
          <img
            src={story.illustrationUrl}
            alt={story.title}
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            loading="lazy"
          />
        </div>
      )}

      <div style={{ padding: '20px 22px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
          <div style={{ flex: 1 }}>
            <h4 style={{ color: '#2d1b69', fontFamily: "'Fredoka One',cursive", fontSize: 18, margin: 0 }}>
              {story.title}
            </h4>
            <div style={{ color: '#9ca3af', fontSize: 12, marginTop: 4 }}>{formatDate(story.createdAt)}</div>
          </div>
        </div>

        {/* Theme */}
        {story.theme && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
            <span style={{ background: '#f0e8ff', color: '#7c3aed', fontSize: 11, fontWeight: 600, borderRadius: 50, padding: '3px 10px' }}>
              {story.theme}
            </span>
          </div>
        )}

        {/* Moral lesson */}
        {story.moralLesson && (
          <div style={{ background: '#fefce8', border: '1px solid #fde68a', borderRadius: 10, padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#92400e' }}>
            <span style={{ fontWeight: 700 }}>💡 Life lesson: </span>{story.moralLesson}
          </div>
        )}

        {/* Read button / story text */}
        {!expanded ? (
          <button
            onClick={() => setExpanded(true)}
            style={{
              background: 'linear-gradient(135deg,#6f42c1,#9b6fe8)',
              color: '#fff',
              border: 'none',
              borderRadius: 50,
              padding: '9px 24px',
              fontWeight: 700,
              fontSize: 13,
              cursor: 'pointer',
              width: '100%',
            }}>
            📖 Read this story
          </button>
        ) : (
          <>
            <div style={{ borderTop: '1px solid #ede9fe', paddingTop: 16, marginTop: 4 }}>
              {paragraphs.map((para, i) => (
                <p key={i} style={{ color: '#374151', lineHeight: 1.85, fontSize: 15, marginBottom: 16 }}>
                  {para}
                </p>
              ))}
            </div>
            <button
              onClick={() => setExpanded(false)}
              style={{
                background: 'none',
                border: '1px solid #ede9fe',
                borderRadius: 50,
                padding: '7px 20px',
                color: '#6f42c1',
                fontWeight: 600,
                fontSize: 12,
                cursor: 'pointer',
                width: '100%',
              }}>
              Close ↑
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const GrandparentViewPage = () => {
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // loading | ok | disabled | notfound | error
  const [child, setChild] = useState(null);
  const [stories, setStories] = useState([]);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    API.get(`/grandparent/view/${token}`)
      .then(res => {
        setChild(res.data.child);
        setStories(res.data.stories || []);
        setStatus('ok');
      })
      .catch(err => {
        const status = err.response?.status;
        const msg    = err.response?.data?.message || 'Something went wrong.';
        if (status === 403) { setErrorMsg(msg); setStatus('disabled'); }
        else if (status === 404) { setErrorMsg(msg); setStatus('notfound'); }
        else { setErrorMsg(msg); setStatus('error'); }
      });
  }, [token]);

  // ── Error / loading states ──────────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <div style={{ minHeight: '100vh', background: '#f8f5ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>🌙</div>
          <div style={{ color: '#6f42c1', fontFamily: "'Fredoka One',cursive", fontSize: 20 }}>Loading stories…</div>
        </div>
      </div>
    );
  }

  if (status !== 'ok') {
    return (
      <div style={{ minHeight: '100vh', background: '#f8f5ff', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <Helmet><title>Dreamy Bedtime Stories</title></Helmet>
        <div style={{ textAlign: 'center', maxWidth: 440 }}>
          <div style={{ fontSize: 72, marginBottom: 16 }}>
            {status === 'disabled' ? '🔒' : '🌙'}
          </div>
          <h2 style={{ color: '#2d1b69', fontFamily: "'Fredoka One',cursive", marginBottom: 12 }}>
            {status === 'disabled' ? 'Link Unavailable' : 'Stories Not Found'}
          </h2>
          <p style={{ color: '#6b7280', marginBottom: 24 }}>{errorMsg}</p>
          <Link to="/" style={{ color: '#6f42c1', fontWeight: 600, textDecoration: 'none' }}>
            🌙 Learn about Dreamy Bedtime →
          </Link>
        </div>
      </div>
    );
  }

  const childFirstName = child.name.split(' ')[0];

  return (
    <div style={{ minHeight: '100vh', background: '#f8f5ff', fontFamily: 'Nunito, Arial, sans-serif' }}>
      <Helmet>
        <title>{child.name}'s Story Library — Dreamy Bedtime</title>
        <meta name="description" content={`Read ${child.name}'s personalised bedtime stories, created just for them.`} />
        <meta name="robots" content="noindex" />
      </Helmet>

      {/* Navbar */}
      <nav style={{
        background: 'linear-gradient(135deg,#2d1b69,#4a2999)',
        padding: '16px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontFamily: "'Fredoka One',cursive", color: '#fff', fontSize: 20 }}>🌙 Dreamy Bedtime</span>
        </Link>
        <Link to="/gift" style={{
          background: '#f9c74f',
          color: '#2d1b69',
          fontWeight: 700,
          fontSize: 13,
          padding: '8px 18px',
          borderRadius: 50,
          textDecoration: 'none',
        }}>
          🎁 Gift a Subscription
        </Link>
      </nav>

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '32px 20px 60px' }}>

        {/* Child hero */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{
            width: 96, height: 96, borderRadius: '50%',
            background: 'linear-gradient(135deg,#f0e8ff,#e8d5ff)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 48, margin: '0 auto 16px',
            boxShadow: '0 4px 20px rgba(111,66,193,0.2)',
          }}>
            {child.avatar}
          </div>
          <h1 style={{ fontFamily: "'Fredoka One',cursive", color: '#2d1b69', fontSize: 32, marginBottom: 6 }}>
            {child.name}'s Stories
          </h1>
          <p style={{ color: '#7c3aed', fontSize: 15, marginBottom: 12 }}>
            Age {child.age}
            {child.interests?.length > 0 && ` · Loves ${child.interests.slice(0, 3).join(', ')}`}
          </p>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#f0e8ff', borderRadius: 50, padding: '6px 16px', color: '#6f42c1', fontSize: 13, fontWeight: 600 }}>
            <span>📚</span>
            <span>{stories.length} {stories.length === 1 ? 'story' : 'stories'} in the library</span>
          </div>
        </div>

        {/* Top gift CTA */}
        <div style={{ marginBottom: 40 }}>
          <GiftBanner childName={childFirstName} />
        </div>

        {/* Stories grid */}
        {stories.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>📖</div>
            <h3 style={{ color: '#2d1b69', fontFamily: "'Fredoka One',cursive", marginBottom: 8 }}>No stories yet</h3>
            <p style={{ color: '#6b7280' }}>{childFirstName}'s story library is empty — check back soon!</p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 24,
            marginBottom: 48,
          }}>
            {stories.map(story => (
              <StoryCard key={story._id} story={story} />
            ))}
          </div>
        )}

        {/* Bottom gift CTA */}
        {stories.length > 0 && (
          <div style={{ marginBottom: 40 }}>
            <GiftBanner childName={childFirstName} />
          </div>
        )}

        {/* Footer */}
        <div style={{ textAlign: 'center', paddingTop: 24, borderTop: '1px solid #ede9fe' }}>
          <p style={{ color: '#9ca3af', fontSize: 13 }}>
            These stories were created with{' '}
            <Link to="/" style={{ color: '#6f42c1', fontWeight: 700, textDecoration: 'none' }}>🌙 Dreamy Bedtime</Link>
            {' '}— AI-powered personalised bedtime stories for children.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginTop: 12 }}>
            <Link to="/pricing" style={{ color: '#6f42c1', fontSize: 12, textDecoration: 'none' }}>Plans & Pricing</Link>
            <Link to="/gift" style={{ color: '#6f42c1', fontSize: 12, textDecoration: 'none' }}>Gift a Subscription</Link>
            <Link to="/privacy" style={{ color: '#6f42c1', fontSize: 12, textDecoration: 'none' }}>Privacy Policy</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GrandparentViewPage;
